import React from 'react';

/**
 * LockedIn sport category glyphs — Sprint 12 WI-2.
 * Generic sport icons only. No team, league, or school marks.
 * Uses currentColor so SportIcon can tint via theme accent classes.
 */

function Glyph({ children, viewBox = '0 0 24 24', ...props }) {
  return (
    <svg
      viewBox={viewBox}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      {...props}
    >
      {children}
    </svg>
  );
}

export function BasketballGlyph(props) {
  return (
    <Glyph {...props}>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.75" />
      <path d="M12 3v18M3 12h18" stroke="currentColor" strokeWidth="1.5" />
      <path d="M5.5 5.5c3 2 3 11 0 13M18.5 5.5c-3 2-3 11 0 13" stroke="currentColor" strokeWidth="1.25" />
    </Glyph>
  );
}

export function FootballGlyph(props) {
  return (
    <Glyph {...props}>
      <ellipse cx="12" cy="12" rx="5" ry="9" transform="rotate(-35 12 12)" stroke="currentColor" strokeWidth="1.75" />
      <path d="M9.5 8.5l5 7M11 10.5l3.5 5" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
    </Glyph>
  );
}

export function SoccerGlyph(props) {
  return (
    <Glyph {...props}>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.75" />
      <path
        d="M12 4.5l2.2 3.8H9.8L12 4.5zM7 9.5l3.8 2.2V8.3L7 9.5zM17 9.5l-3.8 2.2V8.3L17 9.5zM8.5 16l3.5-2v3.8L8.5 16zM15.5 16l-3.5-2v3.8l3.5-1.8z"
        fill="currentColor"
        opacity="0.9"
      />
    </Glyph>
  );
}

export function BaseballGlyph(props) {
  return (
    <Glyph {...props}>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.75" />
      <path
        d="M6.5 6.5c2.5 2.5 2.5 8.5 0 11M17.5 6.5c-2.5 2.5-2.5 8.5 0 11"
        stroke="currentColor"
        strokeWidth="1.35"
        strokeLinecap="round"
      />
    </Glyph>
  );
}

export function HockeyGlyph(props) {
  return (
    <Glyph {...props}>
      <ellipse cx="12" cy="17" rx="7" ry="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8 17V9l4-3 4 3v8" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round" />
      <path d="M10 11h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </Glyph>
  );
}

export function BoxingGlyph(props) {
  return (
    <Glyph {...props}>
      <path
        d="M6 14c0 2.5 2 4.5 4.5 4.5H11c2.5 0 4.5-2 4.5-4.5v-2.5c0-1.4-1.1-2.5-2.5-2.5H8.5C7.1 9 6 10.1 6 11.5V14z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M8 9V7.5A2.5 2.5 0 0 1 10.5 5h1A2.5 2.5 0 0 1 14 7.5V9" stroke="currentColor" strokeWidth="1.4" />
      <path d="M10 16l-4-3M14 16l4-3" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
      <circle cx="12" cy="12" r="1.2" fill="currentColor" />
    </Glyph>
  );
}

export function MmaGlyph(props) {
  return (
    <Glyph {...props}>
      <path
        d="M7.5 11.5c0 2 1.6 3.5 3.5 3.5h2c1.9 0 3.5-1.5 3.5-3.5V9.5c0-1.2-1-2.2-2.2-2.2h-4.6c-1.2 0-2.2 1-2.2 2.2v2z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path d="M9 9V7.2A2 2 0 0 1 11 5.5h2A2 2 0 0 1 15 7.2V9" stroke="currentColor" strokeWidth="1.35" />
      <path d="M5.5 10.5l2 1.5M18.5 10.5l-2 1.5" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
      <path d="M8.5 14.5h7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </Glyph>
  );
}

export function CollegeGlyph(props) {
  return (
    <Glyph {...props}>
      <path
        d="M12 4.5 5 8.5v6.5c0 1.4 3.1 2.5 7 2.5s7-1.1 7-2.5V8.5L12 4.5z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M5 8.5 12 12.5 19 8.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M19 8.5v5.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M9.5 14.5h5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </Glyph>
  );
}

export function GenericSportGlyph(props) {
  return (
    <Glyph {...props}>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.75" />
      <path d="M8 12h8M12 8v8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </Glyph>
  );
}

/** @type {Record<string, React.ComponentType<{ className?: string }>>} */
export const SPORT_GLYPHS = {
  basketball: BasketballGlyph,
  football: FootballGlyph,
  soccer: SoccerGlyph,
  baseball: BaseballGlyph,
  hockey: HockeyGlyph,
  boxing: BoxingGlyph,
  mma: MmaGlyph,
  college: CollegeGlyph,
  generic: GenericSportGlyph,
};
